import React, { useState, useEffect } from 'react';
import { FaTrophy, FaStar, FaFire, FaGift } from 'react-icons/fa';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

function LeaderboardsPage() {
  const { getAdminToken } = useAuth();
  const [topUsers, setTopUsers] = useState([]);
  const [topBusinesses, setTopBusinesses] = useState([]);
  const [topByPoints, setTopByPoints] = useState([]);
  const [topByBadges, setTopByBadges] = useState([]);
  const [topByRevenue, setTopByRevenue] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboards();
  }, []);

  const fetchLeaderboards = async () => {
    try {
      // Fetch all users
      const { data: usersRes } = await supabase.rpc('admin_get_top_users', { p_admin_token: getAdminToken(), p_limit: 1000 });
      const usersData = usersRes?.success ? usersRes.rows : [];

      // Single query for all user check-in counts
      const { data: userCheckIns } = await supabase
        .from('check_ins')
        .select('user_id');
      const userCheckinMap = {};
      userCheckIns?.forEach(r => { userCheckinMap[r.user_id] = (userCheckinMap[r.user_id] || 0) + 1; });

      // Single query for all user badge counts
      const { data: userBadges } = await supabase
        .from('user_badges')
        .select('user_id');
      const userBadgeMap = {};
      userBadges?.forEach(r => { userBadgeMap[r.user_id] = (userBadgeMap[r.user_id] || 0) + 1; });

      const usersWithStats = (usersData || []).map(user => ({
        ...user,
        checkins: userCheckinMap[user.id] || 0,
        badges: userBadgeMap[user.id] || 0,
      }));

      // TOP 5 by check-ins
      setTopUsers([...usersWithStats].sort((a, b) => b.checkins - a.checkins).slice(0, 5));
      // TOP 5 by points
      setTopByPoints([...usersWithStats].sort((a, b) => (b.points || 0) - (a.points || 0)).slice(0, 5));
      // TOP 5 by badges
      setTopByBadges([...usersWithStats].sort((a, b) => b.badges - a.badges).slice(0, 5));

      // Fetch active businesses
      const { data: businessesData } = await supabase
        .from('businesses')
        .select('id, name, city')
        .eq('is_active', true);

      // Single query for all business check-in counts + unique users
      const { data: bizCheckIns } = await supabase
        .from('check_ins')
        .select('business_id, user_id');
      const bizCheckinMap = {};
      const bizUniqueUsersMap = {};
      bizCheckIns?.forEach(r => {
        if (!r.business_id) return;
        bizCheckinMap[r.business_id] = (bizCheckinMap[r.business_id] || 0) + 1;
        if (!bizUniqueUsersMap[r.business_id]) bizUniqueUsersMap[r.business_id] = new Set();
        bizUniqueUsersMap[r.business_id].add(r.user_id);
      });

      // Single query for all business revenues
      const { data: revRes } = await supabase.rpc('admin_get_revenue_logs_full', { p_admin_token: getAdminToken() });
      const revenueLogs = revRes?.success ? revRes.rows : [];
      const bizRevenueMap = {};
      revenueLogs?.forEach(r => {
        if (!r.business_id) return;
        bizRevenueMap[r.business_id] = (bizRevenueMap[r.business_id] || 0) + parseFloat(r.amount || 0);
      });

      const businessesWithStats = (businessesData || []).map(b => ({
        ...b,
        checkins: bizCheckinMap[b.id] || 0,
        uniqueUsers: bizUniqueUsersMap[b.id]?.size || 0,
        revenue: bizRevenueMap[b.id] || 0,
      }));

      // TOP 5 businesses by check-ins
      setTopBusinesses([...businessesWithStats].sort((a, b) => b.checkins - a.checkins).slice(0, 5));
      // TOP 5 businesses by revenue
      setTopByRevenue([...businessesWithStats].sort((a, b) => b.revenue - a.revenue).slice(0, 5));
    } catch (error) {
      console.error('Error fetching leaderboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMedalColor = (rank) => {
    switch (rank) {
      case 1:
        return '#FFD700'; // Gold
      case 2:
        return '#C0C0C0'; // Silver
      case 3:
        return '#CD7F32'; // Bronze
      default:
        return '#FF6B35';
    }
  };

  const getMedalIcon = (rank) => {
    switch (rank) {
      case 1:
        return '🥇';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      default:
        return `#${rank}`;
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Yüklənir...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🏆 TOP 5 Leaderboards</h1>
          <p style={styles.subtitle}>Ən yaxşı performans göstərən istifadəçilər və business-lər</p>
        </div>
        <div style={styles.giftBanner}>
          <FaGift size={24} color="#FF6B35" />
          <span style={styles.giftText}>TOP 5-ə xüsusi hədiyyələr verilir!</span>
        </div>
      </div>

      {/* Users Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <FaTrophy color="#FFD700" /> TOP 5 İstifadəçilər
        </h2>

        <div style={styles.leaderboardsGrid}>
          {/* By Check-ins */}
          <div style={styles.leaderboardCard}>
            <h3 style={styles.cardTitle}>
              <FaFire color="#F44336" /> Ən Çox Check-in
            </h3>
            <div style={styles.list}>
              {topUsers.map((user, index) => (
                <div key={user.id} style={styles.listItem}>
                  <div
                    style={{
                      ...styles.rank,
                      backgroundColor: getMedalColor(index + 1),
                    }}
                  >
                    {getMedalIcon(index + 1)}
                  </div>
                  <img
                    src={
                      user.avatar_url ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        user.full_name
                      )}&background=FF6B35&color=fff&size=50`
                    }
                    alt={user.full_name}
                    style={styles.avatar}
                  />
                  <div style={styles.info}>
                    <p style={styles.name}>{user.full_name}</p>
                    <p style={styles.username}>@{user.username}</p>
                  </div>
                  <div style={styles.score}>
                    <span style={styles.scoreValue}>{user.checkins}</span>
                    <span style={styles.scoreLabel}>check-in</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By Points */}
          <div style={styles.leaderboardCard}>
            <h3 style={styles.cardTitle}>
              <FaStar color="#FFD700" /> Ən Çox Xal
            </h3>
            <div style={styles.list}>
              {topByPoints.map((user, index) => (
                <div key={user.id} style={styles.listItem}>
                  <div
                    style={{
                      ...styles.rank,
                      backgroundColor: getMedalColor(index + 1),
                    }}
                  >
                    {getMedalIcon(index + 1)}
                  </div>
                  <img
                    src={
                      user.avatar_url ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        user.full_name
                      )}&background=FF6B35&color=fff&size=50`
                    }
                    alt={user.full_name}
                    style={styles.avatar}
                  />
                  <div style={styles.info}>
                    <p style={styles.name}>{user.full_name}</p>
                    <p style={styles.username}>@{user.username}</p>
                  </div>
                  <div style={styles.score}>
                    <span style={styles.scoreValue}>{user.points || 0}</span>
                    <span style={styles.scoreLabel}>xal</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By Badges */}
          <div style={styles.leaderboardCard}>
            <h3 style={styles.cardTitle}>
              <FaTrophy color="#9C27B0" /> Ən Çox Badge
            </h3>
            <div style={styles.list}>
              {topByBadges.map((user, index) => (
                <div key={user.id} style={styles.listItem}>
                  <div
                    style={{
                      ...styles.rank,
                      backgroundColor: getMedalColor(index + 1),
                    }}
                  >
                    {getMedalIcon(index + 1)}
                  </div>
                  <img
                    src={
                      user.avatar_url ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        user.full_name
                      )}&background=FF6B35&color=fff&size=50`
                    }
                    alt={user.full_name}
                    style={styles.avatar}
                  />
                  <div style={styles.info}>
                    <p style={styles.name}>{user.full_name}</p>
                    <p style={styles.username}>@{user.username}</p>
                  </div>
                  <div style={styles.score}>
                    <span style={styles.scoreValue}>{user.badges}</span>
                    <span style={styles.scoreLabel}>badge</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Businesses Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <FaTrophy color="#FFD700" /> TOP 5 Business-lər
        </h2>

        <div style={styles.leaderboardsGrid}>
          {/* By Check-ins */}
          <div style={styles.leaderboardCard}>
            <h3 style={styles.cardTitle}>
              <FaFire color="#F44336" /> Ən Çox Ziyarət
            </h3>
            <div style={styles.list}>
              {topBusinesses.map((business, index) => (
                <div key={business.id} style={styles.listItem}>
                  <div
                    style={{
                      ...styles.rank,
                      backgroundColor: getMedalColor(index + 1),
                    }}
                  >
                    {getMedalIcon(index + 1)}
                  </div>
                  <div style={styles.businessIcon}>🏢</div>
                  <div style={styles.info}>
                    <p style={styles.name}>{business.name}</p>
                    <p style={styles.username}>{business.city}</p>
                  </div>
                  <div style={styles.score}>
                    <span style={styles.scoreValue}>{business.checkins}</span>
                    <span style={styles.scoreLabel}>ziyarət</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By Unique Users */}
          <div style={styles.leaderboardCard}>
            <h3 style={styles.cardTitle}>
              <FaStar color="#2196F3" /> Ən Çox Müştəri
            </h3>
            <div style={styles.list}>
              {topBusinesses.map((business, index) => (
                <div key={business.id} style={styles.listItem}>
                  <div
                    style={{
                      ...styles.rank,
                      backgroundColor: getMedalColor(index + 1),
                    }}
                  >
                    {getMedalIcon(index + 1)}
                  </div>
                  <div style={styles.businessIcon}>🏢</div>
                  <div style={styles.info}>
                    <p style={styles.name}>{business.name}</p>
                    <p style={styles.username}>{business.city}</p>
                  </div>
                  <div style={styles.score}>
                    <span style={styles.scoreValue}>{business.uniqueUsers}</span>
                    <span style={styles.scoreLabel}>müştəri</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By Revenue */}
          <div style={styles.leaderboardCard}>
            <h3 style={styles.cardTitle}>
              <FaTrophy color="#4CAF50" /> Ən Çox Gəlir
            </h3>
            <div style={styles.list}>
              {topByRevenue.map((business, index) => (
                <div key={business.id} style={styles.listItem}>
                  <div
                    style={{
                      ...styles.rank,
                      backgroundColor: getMedalColor(index + 1),
                    }}
                  >
                    {getMedalIcon(index + 1)}
                  </div>
                  <div style={styles.businessIcon}>🏢</div>
                  <div style={styles.info}>
                    <p style={styles.name}>{business.name}</p>
                    <p style={styles.username}>{business.city}</p>
                  </div>
                  <div style={styles.score}>
                    <span style={styles.scoreValue}>{business.revenue.toFixed(0)}</span>
                    <span style={styles.scoreLabel}>₼</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Gift Info */}
      <div style={styles.giftInfo}>
        <FaGift size={40} color="#FF6B35" />
        <div style={styles.giftContent}>
          <h3 style={styles.giftTitle}>🎁 Hədiyyə Proqramı</h3>
          <p style={styles.giftText}>
            TOP 5-ə daxil olan istifadəçilər və business-lər xüsusi hədiyyələr qazanırlar!
            Bu leaderboard-lar hər gün yenilənir.
          </p>
          <div style={styles.giftBadges}>
            <span style={styles.giftBadge}>🥇 1-ci Yer: Premium Xüsusi Hədiyyə</span>
            <span style={styles.giftBadge}>🥈 2-ci Yer: Xüsusi Hədiyyə</span>
            <span style={styles.giftBadge}>🥉 3-cü Yer: Hədiyyə</span>
            <span style={styles.giftBadge}>🏅 4-5: Təşəkkür Hədiyyəsi</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1800px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  giftBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '15px 25px',
    backgroundColor: '#FFF3E0',
    borderRadius: '25px',
    border: '2px solid #FF6B35',
  },
  giftText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1A1A2E',
  },
  section: {
    marginBottom: '40px',
  },
  sectionTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  leaderboardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '25px',
  },
  leaderboardCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '25px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px',
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
    transition: 'transform 0.2s',
  },
  rank: {
    width: '45px',
    height: '45px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#fff',
    flexShrink: 0,
  },
  avatar: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
  },
  businessIcon: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    backgroundColor: '#E3F2FD',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    flexShrink: 0,
  },
  info: {
    flex: 1,
  },
  name: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '3px',
  },
  username: {
    fontSize: '13px',
    color: '#999',
  },
  score: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px 15px',
    backgroundColor: '#fff',
    borderRadius: '8px',
  },
  scoreValue: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#FF6B35',
  },
  scoreLabel: {
    fontSize: '11px',
    color: '#666',
  },
  giftInfo: {
    display: 'flex',
    gap: '30px',
    padding: '40px',
    backgroundColor: '#FFF3E0',
    borderRadius: '16px',
    border: '3px solid #FF6B35',
    alignItems: 'center',
    marginTop: '40px',
  },
  giftContent: {
    flex: 1,
  },
  giftTitle: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '10px',
  },
  giftBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '15px',
  },
  giftBadge: {
    padding: '8px 15px',
    backgroundColor: '#FF6B35',
    color: '#fff',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

export default LeaderboardsPage;